// Cosmetic dune grass: instanced blades that follow the terrain and lean and ripple in the shared
// wind (wind.ts). Human-facing only; the simulation and the quadruped know nothing of it.
//
// On a beach island the grass is a thin fringe of pale sea oats: it only grows where the ground
// stands above the dry line (setDryLine, normally the high-tide mark plus a margin), in clumps
// with bare sand between, and shrinks to nothing as it nears that line.
//
// One fixed set of tufts lives on a lattice that repeats every `tile` metres. The vertex
// shader wraps each tuft around the camera (toroidal), so the grass always surrounds the
// viewer with zero CPU work per frame and tufts only ever appear or vanish at the far fade
// edge, where they have shrunk to nothing. Everything else is also done on the GPU: ground
// height (sampled bilinearly from the same heightfield the terrain mesh uses), meadow
// patchiness, colour, bending and the wind.
//
// Cost control. The vertex shader runs for every tuft of the lattice every frame, but only a
// small share of them can show: the lattice is a square around a circular fade, half of it is
// behind the viewer, and the meadow density gate drops about half of what is left. So each
// tuft decides first, from cheap tests (fade radius, world bounds, a coarse view-frustum
// test), and a rejected tuft skips the density noise, the ground sample and the wind entirely
// (all 24 of its vertices take the same branch, so the GPU does not diverge). Seeds are also
// sorted by their keep threshold, so cutting the instance count (setQuality) thins the meadow
// evenly instead of dropping tufts at random.
//
// Blades are three-segment ribbons, three per tuft. They are lit with an up-biased normal so
// the meadow shades like the ground it grows from, and double-sided without the usual back
// face flip (which would make half of them dark).
import * as THREE from 'three';
import { WindField, WIND_GLSL } from './wind';
import { CanopyShade, CANOPY_SHADE_DECL, canopyShadeApply } from './canopyShade';

export interface TerrainField { z: Float32Array; nx: number; ny: number; width: number; height: number }

const BLADES = 3;
const SEGS = 3;

// Look tuning (metres, sRGB colours 0..1)
const FADE_START = 16.0;       // blades start shrinking beyond this distance from the eye
const FADE_END = 28.0;         // and are gone by here
const BLADE_WIDTH = 0.05;
const H_MIN = 0.30, H_MAX = 0.95;
const DRY_FADE = 0.35;         // metres of ground height over which grass fades in above the dry line

function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
}

function makeBladeGeometry(): THREE.BufferGeometry {
  const r = rng(0x51f15e);
  const pos: number[] = [], nor: number[] = [], blade: number[] = [], idx: number[] = [];
  for (let b = 0; b < BLADES; b++) {
    // root offset inside the tuft, blade facing, height scale
    const a = (b / BLADES) * Math.PI * 2 + r() * 1.2;
    const rad = 0.05 + 0.12 * r();
    const ox = Math.cos(a) * rad, oz = Math.sin(a) * rad;
    const yaw = r() * Math.PI * 2;
    const hs = 0.75 + 0.5 * r();
    const base = pos.length / 3;
    for (let s = 0; s <= SEGS; s++) {
      for (const x of [-0.5, 0.5]) {
        pos.push(x, s / SEGS, 0);
        nor.push(0, 0, 1);                 // must exist (else flat shading); the shader replaces it
        blade.push(ox, oz, yaw, hs);
      }
    }
    for (let s = 0; s < SEGS; s++) {
      const i = base + s * 2;
      idx.push(i, i + 1, i + 2, i + 1, i + 3, i + 2);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('aBlade', new THREE.Float32BufferAttribute(blade, 4));
  g.setIndex(idx);
  return g;
}

const GRASS_DECL = /* glsl */`
attribute vec4 aSeed;
attribute vec4 aBlade;
uniform vec3 uCam;
uniform float uTile;
uniform vec2 uFade;
uniform vec4 uBounds;
uniform float uDry;
uniform sampler2D uField;
uniform vec4 uFieldInfo;
varying vec3 vGrassCol;
varying vec3 vGrassPos;

float ghash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float gnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(ghash(i), ghash(i + vec2(1.0, 0.0)), f.x),
             mix(ghash(i + vec2(0.0, 1.0)), ghash(i + vec2(1.0, 1.0)), f.x), f.y);
}
// Ground height (three y) at three-space (x, z): same bilinear sample as TerrainView.heightAtThree.
float groundY(vec2 xz) {
  vec2 uv = vec2(xz.x / uFieldInfo.z * (uFieldInfo.x - 1.0), -xz.y / uFieldInfo.w * (uFieldInfo.y - 1.0));
  uv = clamp(uv, vec2(0.0), uFieldInfo.xy - 1.0001);
  ivec2 i = ivec2(floor(uv));
  vec2 f = uv - vec2(i);
  float a = texelFetch(uField, i, 0).r;
  float b = texelFetch(uField, i + ivec2(1, 0), 0).r;
  float c = texelFetch(uField, i + ivec2(0, 1), 0).r;
  float d = texelFetch(uField, i + ivec2(1, 1), 0).r;
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
` + WIND_GLSL;

export interface GrassOptions {
  tufts?: number;
  bounds?: [number, number];
  /** Plant canopy shading map; grass is darkened where the canopy shades the ground. */
  shade?: CanopyShade;
}

export class GrassView {
  private readonly scene: THREE.Scene;
  private readonly mesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly base: THREE.BufferGeometry;
  private readonly material: THREE.MeshStandardMaterial;
  private fieldTex: THREE.DataTexture | null = null;
  private readonly shade: CanopyShade;
  private readonly ownsShade: boolean;
  private bw = 128; private bh = 128;
  private total = 0;
  private readonly u = {
    uCam:       { value: new THREE.Vector3() },
    uTile:      { value: FADE_END * 2 + 4 },
    uFade:      { value: new THREE.Vector2(FADE_START, FADE_END) },
    uBounds:    { value: new THREE.Vector4(-1e6, -1e6, 1e6, 1e6) },
    uDry:       { value: -1e6 },
    uField:     { value: null as THREE.Texture | null },
    uFieldInfo: { value: new THREE.Vector4(2, 2, 1, 1) },
  };

  /** `bounds` = world width and height in metres (sim x and y); grass stays inside them. */
  constructor(scene: THREE.Scene, wind: WindField, opts: GrassOptions = {}) {
    this.scene = scene;
    this.ownsShade = !opts.shade;
    this.shade = opts.shade ?? new CanopyShade(opts.bounds?.[0] ?? 1, opts.bounds?.[1] ?? 1);
    const tufts = opts.tufts ?? 40000;
    this.base = makeBladeGeometry();
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = this.base.index;
    for (const n of Object.keys(this.base.attributes)) this.geo.setAttribute(n, this.base.attributes[n]);
    const r = rng(0xbadc0de);
    const raw = new Float32Array(tufts * 4);
    for (let i = 0; i < raw.length; i++) raw[i] = r();
    // sort by the keep threshold (aSeed.z) so the first N instances are the ones kept in the
    // thinnest meadow: dropping the tail lowers density everywhere without holes
    const order = new Uint32Array(tufts);
    for (let i = 0; i < tufts; i++) order[i] = i;
    order.sort((a, b) => raw[a * 4 + 2] - raw[b * 4 + 2]);
    const seed = new Float32Array(tufts * 4);
    for (let i = 0; i < tufts; i++) seed.set(raw.subarray(order[i] * 4, order[i] * 4 + 4), i * 4);
    this.geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 4));
    this.total = tufts;
    this.geo.instanceCount = tufts;

    if (opts.bounds) this.setBounds(opts.bounds[0], opts.bounds[1]);
    this.setTerrain(null);

    const m = new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0.0, side: THREE.DoubleSide });
    m.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this.u, wind.uniforms, this.shade.uniforms);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\n' + GRASS_DECL)
        .replace('#include <beginnormal_vertex>', /* glsl */`
          // --- where this tuft is: its lattice cell, wrapped around the camera ---
          vec2 gc = aSeed.xy * uTile;
          gc = mod(gc - uCam.xz + 0.5 * uTile, uTile) - 0.5 * uTile + uCam.xz;
          float tr = aSeed.w * 6.2831853;
          vec2 goff = vec2(cos(tr) * aBlade.x - sin(tr) * aBlade.y, sin(tr) * aBlade.x + cos(tr) * aBlade.y);
          vec2 gp = gc + goff;
          float gdist = length(gp - uCam.xz);
          float gfar = 1.0 - smoothstep(uFade.x, uFade.y, gdist);
          float ginb = step(uBounds.x, gp.x) * step(gp.x, uBounds.z) * step(uBounds.y, gp.y) * step(gp.y, uBounds.w);
          // Coarse frustum test at about ground height under the eye. Generous margins (hills move the
          // real ground tens of centimetres to metres off this estimate), so it only drops tufts that
          // are clearly behind or to the side of the view.
          vec4 gclip = projectionMatrix * (viewMatrix * vec4(gp.x, uCam.y - 2.0, gp.y, 1.0));
          float gvis = 1.0;
          if (gclip.w < -1.0 || abs(gclip.x) > gclip.w * 1.25 + 1.0 || abs(gclip.y) > gclip.w * 1.6 + 2.0) gvis = 0.0;
          float ghgt = 0.0;
          float gground = -1.0e4;
          if (gvis * ginb * gfar > 0.0) {
            // clumps: a few thick tussocks with bare sand between them
            float gdens = mix(0.08, 1.0, smoothstep(0.32, 0.68, gnoise(gp * 0.16)))
                        * mix(0.55, 1.0, gnoise(gp * 0.7 + 11.0));
            float gkeep = step(aSeed.z, gdens * (1.0 - smoothstep(uFade.x * 0.7, uFade.y, gdist)));
            if (gkeep > 0.0) {
              gground = groundY(gp);
              float gdryW = smoothstep(uDry, uDry + ${DRY_FADE.toFixed(2)}, gground);   // nothing below the tide line
              ghgt = mix(${H_MIN.toFixed(2)}, ${H_MAX.toFixed(2)}, fract(aSeed.z * 17.3 + aSeed.w * 5.1))
                   * aBlade.w * (0.6 + 0.6 * gnoise(gp * 0.23 + 7.0)) * gfar * gdryW;
            }
          }
          float gyaw = aBlade.z + tr;
          vec3 gright = vec3(cos(gyaw), 0.0, sin(gyaw));
          vec3 objectNormal = normalize(vec3(-gright.z * 0.35, 1.0, gright.x * 0.35));
        `)
        .replace('#include <begin_vertex>', /* glsl */`
          float gy = position.y;
          // a rejected tuft collapses to one point below the world: zero-area triangles are dropped
          // before rasterisation, and none of the work below runs for it
          vec3 transformed = vec3(0.0, -1.0e4, 0.0);
          vGrassCol = vec3(0.0);
          vGrassPos = transformed;
          if (ghgt > 0.0) {
            // static lean + wind push + a little flutter, all growing with height squared
            vec2 gpush = windPush(gp);
            float gfl = sin(uWindT * (3.0 + 2.0 * aSeed.w) + aSeed.z * 50.0 + gp.x * 0.7);
            vec2 gleanDir = vec2(cos(gyaw + 1.5708), sin(gyaw + 1.5708));
            vec2 gperp = vec2(-uWindDir.y, uWindDir.x);
            vec2 go = (gleanDir * (0.12 + 0.30 * fract(aSeed.x * 31.7))
                       + gpush * 0.55
                       + gperp * (0.07 * gfl * (0.3 + length(gpush)))) * gy * gy * ghgt;
            float ghh = gy * ghgt;
            float gvy = sqrt(max(ghh * ghh - dot(go, go), 0.0));          // bending keeps the blade's length
            float gw = ${BLADE_WIDTH.toFixed(3)} * (1.0 - 0.92 * gy) * smoothstep(0.0, 0.05, ghgt);
            transformed = vec3(gp.x, gground - 0.01, gp.y) + vec3(go.x, gvy, go.y) + gright * (position.x * gw);
            // colour: dark at the root, greener or drier toward the tip, per-blade variation
            float gstraw = gnoise(gp * 0.09 + 3.0);
            vec3 gtip = mix(vec3(0.46, 0.52, 0.24), vec3(0.80, 0.74, 0.46), smoothstep(0.30, 0.70, gstraw));
            vec3 gcol = mix(vec3(0.20, 0.22, 0.09), gtip, pow(gy, 0.8)) * (0.8 + 0.4 * fract(aSeed.x * 13.7));
            vGrassCol = pow(gcol, vec3(2.2));
            vGrassPos = transformed;
          }
        `);
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vGrassCol;\nvarying vec3 vGrassPos;\n' + CANOPY_SHADE_DECL)
        .replace('#include <color_fragment>', 'diffuseColor.rgb *= vGrassCol;')
        .replace('#include <lights_fragment_end>', '#include <lights_fragment_end>\n' + canopyShadeApply('vGrassPos'))
        .replace('#include <normal_fragment_begin>', '#include <normal_fragment_begin>\nnormal = normalize(vNormal);');
    };
    this.material = m;
    this.mesh = new THREE.Mesh(this.geo, m);
    this.mesh.frustumCulled = false;      // positions are computed on the GPU
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = true;
    scene.add(this.mesh);
  }

  /** Keep grass inside the world footprint (sim width x height metres). */
  setBounds(width: number, height: number) {
    this.bw = width; this.bh = height;
    // three x = sim x; three z = -sim y
    this.u.uBounds.value.set(0, -height, width, 0);
  }

  /** Use the terrain heightfield for ground height; null = flat ground at z = 0. */
  setTerrain(f: TerrainField | null, width = this.bw, height = this.bh) {
    if (this.fieldTex) { this.fieldTex.dispose(); this.fieldTex = null; }
    // Copy: the texture owns its buffer, and this keeps the array type a plain ArrayBuffer view.
    let data: Float32Array<ArrayBuffer>, nx: number, ny: number, w: number, h: number;
    if (f) { data = new Float32Array(f.z); nx = f.nx; ny = f.ny; w = f.width; h = f.height; }
    else { data = new Float32Array(4); nx = 2; ny = 2; w = width; h = height; }
    const t = new THREE.DataTexture(data, nx, ny, THREE.RedFormat, THREE.FloatType);
    t.minFilter = THREE.NearestFilter; t.magFilter = THREE.NearestFilter;
    t.generateMipmaps = false; t.needsUpdate = true;
    this.fieldTex = t;
    this.u.uField.value = t;
    this.u.uFieldInfo.value.set(nx, ny, w, h);
    this.setBounds(w, h);
  }

  /** Follow the eye. Call once per frame (the wind is advanced by whoever owns it). */
  update(camPos: THREE.Vector3) {
    this.u.uCam.value.copy(camPos);
  }

  /**
   * Fraction (0..1) of the lattice's tufts to draw. Because seeds are sorted by keep threshold
   * this thins the meadow evenly. Cheap to call; the renderer's quality controller uses it.
   */
  setQuality(q: number) {
    this.geo.instanceCount = Math.max(0, Math.min(this.total, Math.floor(this.total * q)));
  }

  /**
   * Ground height (sim z) below which no grass grows; it fades in over DRY_FADE above it. Use the
   * high-tide mark plus a margin on an island. The default (very low) grows grass everywhere.
   */
  setDryLine(h: number) { this.u.uDry.value = h; }

  /** Show or hide the grass (debug toggle). */
  setEnabled(e: boolean) { this.mesh.visible = e; }

  get tufts(): number { return this.geo.instanceCount; }

  dispose() {
    this.scene.remove(this.mesh);
    this.geo.dispose(); this.base.dispose(); this.material.dispose();
    this.fieldTex?.dispose();
    if (this.ownsShade) this.shade.dispose();
  }
}
